/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectActiveTrendTab,
  selectIsCumulative,
  selectIsTrendDeAggregate,
  selectTrendTimeFilterList,
  selectTrendTimeVisuals,
  selectTrendTimeWeeklyView,
  selectIsFocusOnSet,
  selectTrendHotelFocusOn,
  selectTrendTimeDateComparison,
  selectTrendTimeStartDate,
  selectTrendTimeEndDate,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import {
  selectWeeklyTrendEndDate,
  selectWeeklyTrendStartDate,
  selectTrendTargetDate,
  selectEmbedToken,
  selectHotelName,
  selectTabList,
} from 'modules/dashboard/selectors';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import TrendFilter from 'modules/dashboard/components/tab-container/trend-tabs/components/filter-panel';
import { REPORT_TYPE } from 'modules/dashboard/constants';
import { trendActions } from 'modules/dashboard/components/tab-container/trend-tabs/slice';
import { isSameDay } from 'date-fns';
import { compareWindows } from 'modules/common/ui-controller';
import { getWeeklyAlignedCustomDate } from 'modules/common/utils/dateComparisonUtils';
import { formatDate, getVisualName } from 'modules/dashboard/functions';
import VisualCard from 'modules/dashboard/components/tab-container/visual-card';
import TrendTimeSegmentTabularWidget from './trend-time-widget/time-segment-tabular-visual-view';
import TrendHotelSegmentTabularWidget from './trend-hotel-widget/hotel-segment-tabular-visual-view';
import TrendSegmentTabularWidget from './trend-segment-widget/tabular-visual-view';
import TrendHotelTabularWidget from './trend-hotel-widget/tabular-visual-view';
import TrendTimeTabularWidget from './trend-time-widget/tabular-visual-view';
/**
 * Trend tab widget implementation
 * @param {Number} value - Trend time widget tab value
 * @param {Boolean} toggleDisabled - PMS sync toggle disabled state
 * @param {Date} latestDate - Latest booking date of selected hotel
 * @param {String} hotelTimezone - Timezone of selected hotel
 * @param {String} reportType - Report type of active tab
 * @returns
 */
const Widget = ({ value, latestDate, reportType, hotelTimezone, toggleDisabled }) => {
  const dispatch = useDispatch();
  // States are obtained from redux store. Refer each selector function for details.
  const filterList = useSelector(selectTrendTimeFilterList);
  const tokenDetails = useSelector(selectEmbedToken);
  const visuals = useSelector(selectTrendTimeVisuals);
  const activeTab = useSelector(selectActiveTrendTab);

  const weeklyViewToggle = useSelector(selectTrendTimeWeeklyView);
  const startDate = useSelector(selectWeeklyTrendStartDate);
  const endDate = useSelector(selectWeeklyTrendEndDate);
  const targetDate = useSelector(selectTrendTargetDate);
  const isCumulative = useSelector(selectIsCumulative);
  const isTrendDeAggregate = useSelector(selectIsTrendDeAggregate);
  const IsFocusOnSet = useSelector(selectIsFocusOnSet);
  const hotelFocusOn = useSelector(selectTrendHotelFocusOn);
  const selectedHotel = useSelector(selectHotelName);
  const TAB_VALUE = useSelector(selectTabList);
  const dateComparison = useSelector(selectTrendTimeDateComparison);
  const comparisonStartDate = useSelector(selectTrendTimeStartDate);
  const comparisonEndDate = useSelector(selectTrendTimeEndDate);
  //
  const [isLoaded, setIsLoaded] = useState(false);
  // To get visuals based on the configuration
  const getWidget = (index, cssClassName, displayFunction) => {
    const found = visuals?.find((visual) => visual?.order === index);
    //
    return (
      found && (
        <VisualCard
          cssClassName={cssClassName}
          accessToken={tokenDetails?.accessToken}
          embedUrl={found?.embedUrl}
          id={found?.reportId}
          visualName={found?.name}
          pageName={found?.pageName}
          pageDisplayName={found?.pageDisplayName}
          reportName={found?.reportName}
          filters={filterList}
          tab={TAB_VALUE.PBI_TREND}
          dispatch={dispatch}
          displayFunction={displayFunction}
        />
      )
    );
  };
  //
  const [label, setLabel] = useState(null);
  // Triggered when startDate, endDate, targetDate, weeklyViewToggle, isCumulative values change
  useEffect(() => {
    // Show adjusted stay dates for weekly view toggle on
    if (isCumulative) {
      setLabel(null);
      return;
    }
    const labels = [];
    if (
      weeklyViewToggle &&
      (!isSameDay(new Date(startDate), new Date(targetDate.startDate)) ||
        !isSameDay(new Date(endDate), new Date(targetDate.endDate)))
    ) {
      labels.push(
        `Stay date selection adjusted.  New stay date range from: ${formatDate(
          startDate
        )} to ${formatDate(endDate)}`
      );
    }
    // Show adjusted custom comparison dates for weekly view when they differ from
    // the user's original custom selection
    const isWeeklyAdjusted = weeklyViewToggle && !isCumulative;
    if (
      dateComparison === compareWindows.cus &&
      isWeeklyAdjusted &&
      comparisonStartDate &&
      comparisonEndDate
    ) {
      const adjustedComparisonDate = getWeeklyAlignedCustomDate(
        { startDate, endDate },
        targetDate?.endDate,
        { startDate: comparisonStartDate, endDate: comparisonEndDate }
      );
      if (
        !isSameDay(
          new Date(adjustedComparisonDate.startDate),
          new Date(comparisonStartDate)
        ) ||
        !isSameDay(new Date(adjustedComparisonDate.endDate), new Date(comparisonEndDate))
      ) {
        labels.push(
          `Custom comparision date adjusted. Custom comparision date range from: ${formatDate(
            adjustedComparisonDate.startDate
          )} to ${formatDate(adjustedComparisonDate.endDate)}`
        );
      }
    }
    setLabel(labels.length > 0 ? labels.join('\n') : null);
  }, [
    startDate,
    endDate,
    targetDate,
    weeklyViewToggle,
    isCumulative,
    dateComparison,
    comparisonStartDate,
    comparisonEndDate,
  ]);
  //
  return (
    <>
      <Grid
        item
        xs={2}
        md={2}
        cc={2.5}
        sx={{
          ml: {
            xs: 0,
            cc: 0.25,
            md: 0,
          },
        }}
      >
        <TrendFilter
          reportType={reportType}
          visual={getWidget(
            100,
            styles.symmetricComparisonWindow,
            trendActions.setSymmetricComparisonIsDisplay
          )}
          visualName={getVisualName(visuals, 100)}
          tabValue={value}
          toggleDisabled={toggleDisabled}
          latestDate={latestDate}
          hotelTimezone={hotelTimezone}
          isLoaded={isLoaded}
        />
      </Grid>
      {IsFocusOnSet && (
        <Grid container item xs={10} cc={9.25} md={10} direction="column">
          <Grid item container justifyContent="space-around" mt={4}>
            {label && (
              <Typography paddingBottom={1} className={styles.weeklyLabel} sx={{ whiteSpace: 'pre-line' }}>
                {label}
              </Typography>
            )}
          </Grid>
          {activeTab === REPORT_TYPE.TREND_TIME && !isTrendDeAggregate && (
            <TrendTimeTabularWidget setIsTimeLoaded={setIsLoaded} />
          )}
          {activeTab === REPORT_TYPE.TREND_SEGMENT &&
            isTrendDeAggregate &&
            hotelFocusOn?.id !== selectedHotel?.id && (
              <TrendHotelSegmentTabularWidget setIsSegmentLoaded={setIsLoaded} />
            )}
          {activeTab === REPORT_TYPE.TREND_SEGMENT &&
            isTrendDeAggregate &&
            hotelFocusOn?.id === selectedHotel?.id && (
              <TrendTimeSegmentTabularWidget setIsTimeLoaded={setIsLoaded} />
            )}
          {activeTab === REPORT_TYPE.TREND_SEGMENT && !isTrendDeAggregate && (
            <TrendSegmentTabularWidget setIsSegmentLoaded={setIsLoaded} />
          )}
          {activeTab === REPORT_TYPE.TREND_HOTEL && (
            <TrendHotelTabularWidget setIsHotelLoaded={setIsLoaded} />
          )}
        </Grid>
      )}
    </>
  );
};
//
export default Widget;
